import { useAuth0 } from '@auth0/auth0-react';
import { IconNames } from '@blueprintjs/icons';
import { ButtonWithDropdownMenu, DropdownMenu } from '@strata/react-dropdown-menu';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { GridWithFilters } from '@strata/react-grid';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useNavigate, useSearchParams } from 'react-router';

import { DeleteRequestQueueDialog } from 'components/requests/dialogs/DeleteRequestQueueDialog';
import { RequestQueueDialog } from 'components/requests/dialogs/RequestQueueDialog';
import {
    requestsListColumns,
    requestsListFilterCategories,
} from 'components/requests/RequestsListGridConfig';
import { useUserProfile } from 'services/APIHooks';
import { UNASSIGNED_LABEL } from 'services/requestHelpers';
import {
    applyQueueToRequests,
    buildQueueCriteriaOptions,
    BUILT_IN_QUEUE_IDS,
    canMutateQueue,
    partitionQueuesForRail,
    wireSortToGridSort,
} from 'services/requestQueueHelpers';
import { AppRoutes } from 'services/routes';
import { MESSAGES_LOCATION_HASH } from 'services/userSupportHelpers';
import { useGetRequestQueuesQuery, useGetRequestsQuery } from 'store/api/agoraMonitorApi';
import { useGetCompassUserNameMapQuery } from 'store/api/authApi';
import { Grids, setGridActiveFilters, setGridSort, useGridState } from 'store/grids/gridsSlice';

const POLLING_INTERVAL_MS = 20 * 1000;
const initialSortProperties = [{ id: 'urgency', desc: true }];
const QUEUE_QUERY_PARAM = 'queue';

export const RequestsListPage = () => {
    const { showWorkflowManagementLayer = false } = useFlags();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth0();
    const { userProfile } = useUserProfile();
    const viewerUpn = userProfile?.upn ?? user?.email;
    const [queueDialog, setQueueDialog] = useState(null);
    const [queueToDelete, setQueueToDelete] = useState(null);
    const previousQueueIdRef = useRef();

    // isLoading, not isFetching: a background poll should not blank the grid on each refresh.
    const { data: requests, isLoading: requestsLoading } = useGetRequestsQuery(undefined, {
        skip: !showWorkflowManagementLayer,
        pollingInterval: POLLING_INTERVAL_MS,
    });
    const { data: queues, isLoading: queuesLoading } = useGetRequestQueuesQuery(undefined, {
        skip: !showWorkflowManagementLayer,
    });
    const { data: userNameMap } = useGetCompassUserNameMapQuery(undefined, {
        skip: !showWorkflowManagementLayer,
    });

    const selectedQueueId = searchParams.get(QUEUE_QUERY_PARAM) ?? BUILT_IN_QUEUE_IDS.openRequests;
    const selectedQueue =
        queues?.find((queue) => queue.queueId === selectedQueueId) ??
        queues?.find((queue) => queue.queueId === BUILT_IN_QUEUE_IDS.openRequests);
    const appliedQueueId = selectedQueue?.queueId;
    const selectedQueueSortKey = JSON.stringify(selectedQueue?.sort ?? null);

    const {
        activeFilters,
        filterPaneWidth,
        search,
        sort,
        updateFilterPaneWidth,
        updateGridActiveFilters,
        updateGridSearch,
        updateGridSort,
    } = useGridState(Grids.REQUESTS, initialSortProperties);

    // Re-sync sort on queue edit; clear FilterPane only on a real queue switch (not first load).
    // Dispatch slice actions directly: useGridState updaters are new identities every render.
    useEffect(() => {
        if (!appliedQueueId) {
            return;
        }
        dispatch(
            setGridSort({
                grid: Grids.REQUESTS,
                sort: wireSortToGridSort(JSON.parse(selectedQueueSortKey)),
            }),
        );
        if (previousQueueIdRef.current && previousQueueIdRef.current !== appliedQueueId) {
            dispatch(setGridActiveFilters({ grid: Grids.REQUESTS, activeFilters: {} }));
        }
        previousQueueIdRef.current = appliedQueueId;
    }, [dispatch, appliedQueueId, selectedQueueSortKey]);

    const queuedRequests = useMemo(() => {
        if (!requests || !selectedQueue) {
            return undefined;
        }
        return applyQueueToRequests(
            requests,
            { criteria: selectedQueue.criteria, sort: selectedQueue.sort },
            { viewerUpn },
        );
    }, [requests, selectedQueue, viewerUpn]);

    const displayRequests = useMemo(
        () =>
            queuedRequests?.map((request) => ({
                ...request,
                assignedTo:
                    userNameMap?.[request.assignedTo] ?? request.assignedTo ?? UNASSIGNED_LABEL,
            })),
        [queuedRequests, userNameMap],
    );

    const isLoading = requestsLoading || queuesLoading || !selectedQueue;

    const criteriaOptions = useMemo(
        () => (queueDialog ? buildQueueCriteriaOptions(requests) : undefined),
        [queueDialog, requests],
    );

    const handleSelectQueue = useCallback(
        (queueId) => {
            setSearchParams(
                (params) => {
                    const next = new URLSearchParams(params);
                    if (queueId === BUILT_IN_QUEUE_IDS.openRequests) {
                        next.delete(QUEUE_QUERY_PARAM);
                    } else {
                        next.set(QUEUE_QUERY_PARAM, queueId);
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const queueMenuItems = useMemo(() => {
        const { teamQueues, myQueues } = partitionQueuesForRail(queues);
        return [...teamQueues, ...myQueues].map((queue) => ({
            text: queue.name,
            icon: queue.queueId === selectedQueue?.queueId ? IconNames.TICK : undefined,
            onItemClick: () => handleSelectQueue(queue.queueId),
        }));
    }, [queues, selectedQueue?.queueId, handleSelectQueue]);

    const queueActionMenuItems = useMemo(
        () => [
            {
                text: 'Create new queue',
                onItemClick: () => setQueueDialog({}),
            },
            ...(canMutateQueue(selectedQueue, viewerUpn)
                ? [
                      {
                          text: 'Edit queue',
                          onItemClick: () => setQueueDialog({ queue: selectedQueue }),
                      },
                      {
                          text: 'Delete queue',
                          intent: DropdownMenu.Intent.DANGER,
                          onItemClick: () => setQueueToDelete(selectedQueue),
                      },
                  ]
                : []),
        ],
        [selectedQueue, viewerUpn],
    );

    const handleRowClick = ({ referralId }) => {
        if (!referralId) {
            return;
        }
        navigate(
            { pathname: AppRoutes.referralRedirect(referralId), hash: MESSAGES_LOCATION_HASH },
            {
                state: {
                    backToDestination: { text: 'Back to Requests', route: AppRoutes.requestsList },
                },
            },
        );
    };

    const handleDeleteClose = ({ deleted } = {}) => {
        const deletedId = queueToDelete?.queueId;
        setQueueToDelete(null);
        if (deleted && deletedId && deletedId === selectedQueueId) {
            handleSelectQueue(BUILT_IN_QUEUE_IDS.openRequests);
        }
    };

    if (!showWorkflowManagementLayer) {
        return <Navigate replace to={AppRoutes.home} />;
    }

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Requests"
                secondaryTitle={selectedQueue?.name}
                data={displayRequests}
                columns={requestsListColumns}
                isLoading={isLoading}
                sortProperties={sort}
                onSort={updateGridSort}
                enableSearch
                searchText={search}
                onSearch={updateGridSearch}
                filterCategories={requestsListFilterCategories}
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                onRowClick={handleRowClick}
                topRightContent={
                    <div className="sds_referral-grid__refresh" style={{ marginLeft: 'auto' }}>
                        <ButtonWithDropdownMenu
                            label="Queues"
                            intent={ButtonWithDropdownMenu.Intent.PRIMARY}
                            menuItems={queueMenuItems}
                            additionalMenuItems={queueActionMenuItems}
                        />
                    </div>
                }
            />
            {queueDialog && (
                <RequestQueueDialog
                    isOpen
                    queue={queueDialog.queue}
                    criteriaOptions={criteriaOptions}
                    onClose={() => setQueueDialog(null)}
                />
            )}
            {queueToDelete && (
                <DeleteRequestQueueDialog
                    isOpen
                    queue={queueToDelete}
                    onClose={handleDeleteClose}
                />
            )}
        </FormPageContainer>
    );
};
